const ExecutiveTemplate = ({ candidateData }) => {
    if (!candidateData) return null;

    const {
        name,
        email,
        phone,
        address,
        linkedin,
        basic_details,
        employment,
        education,
        projects,
        certifications,
        it_skills,
        candidate_languages,
    } = candidateData;

    // Helper functions
    const safeString = (value) => value ?? "";
    const safeList = (value) => Array.isArray(value) ? value : [];

    const monthNames = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const formatMonthYear = (year, month) => {
        const y = parseInt(year);
        const m = parseInt(month);
        if (!y) return "";
        const monthStr = (m && m >= 1 && m <= 12) ? monthNames[m] : "";
        return monthStr ? `${monthStr} ${y}` : `${y}`;
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return "";
        try {
            const d = new Date(isoDate);
            const day = String(d.getDate()).padStart(2, '0');
            const month = monthNames[d.getMonth() + 1];
            const year = d.getFullYear();
            return `${day} ${month} ${year}`;
        } catch {
            return isoDate;
        }
    };

    const calculateSkillLevel = (years, months) => {
        const y = parseInt(years) || 0;
        const m = parseInt(months) || 0;
        const totalMonths = y * 12 + m;

        if (totalMonths >= 60) return 95;
        if (totalMonths >= 36) return 85;
        if (totalMonths >= 24) return 75;
        if (totalMonths >= 12) return 65;
        return 55;
    };

    const formatExperience = (years, months) => {
        const y = parseInt(years) || 0;
        const m = parseInt(months) || 0;
        if (y === 0 && m === 0) return "";
        return `${y} year${y > 1 ? 's' : ''} ${m} month${m > 1 ? 's' : ''}`;
    };

    const getJobTitle = () => {
        if (!employment || employment.length === 0) return "PROFESSIONAL";
        const lastJob = employment[employment.length - 1];
        return (lastJob?.job_title?.title || "PROFESSIONAL").toUpperCase();
    };

    const getLanguageProficiency = (lang) => {
        const proficiencies = [];
        if (lang?.can_read === 1 || lang?.can_read === "1" || lang?.read === 1) proficiencies.push("Read");
        if (lang?.can_write === 1 || lang?.can_write === "1" || lang?.write === 1) proficiencies.push("Write");
        if (lang?.can_speak === 1 || lang?.can_speak === "1" || lang?.speak === 1) proficiencies.push("Speak");
        return proficiencies.length > 0 ? proficiencies.join(", ") : "Basic";
    };

    const profileSummary = basic_details?.profile_summery || "";
    const profileImage = basic_details?.profile_image;

    return (
        <div className="w-[210mm] min-h-[297mm] max-w-[210mm] mx-auto bg-white shadow-md overflow-hidden box-border relative">
            {/* Header Section - Gray background with name and title */}
            <div className="w-full bg-[#C8C4C4] px-10 py-8 flex items-center">
                {profileImage && (
                    <div className="w-20 h-20 mr-5 rounded-full overflow-hidden flex-shrink-0">
                        <img
                            src={profileImage}
                            alt="Profile"
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}
                <div>
                    <h1 className="text-2xl font-bold tracking-wide text-black uppercase">
                        {safeString(name)}
                    </h1>
                    <p className="text-xs tracking-[2px] text-[#666666] mt-1">
                        {getJobTitle()}
                    </p>
                </div>
            </div>

            {/* Profile Section - Full width if exists */}
            {profileSummary && (
                <>
                    <div className="px-10 py-5 bg-white">
                        <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                            PROFILE
                        </h2>
                        <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-2.5"></div>
                        <p className="text-[10px] text-black leading-relaxed text-justify">
                            {profileSummary}
                        </p>
                    </div>
                    <div className="w-full h-[1px] bg-black"></div>
                </>
            )}

            {/* Two-column layout */}
            <div className="flex">
                {/* Left Sidebar - 35% width */}
                <div className="w-[35%] px-8 py-5 bg-white">
                    {/* CONTACT Section */}
                    <div className="mb-6">
                        <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                            CONTACT
                        </h2>
                        <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-3.5"></div>

                        {phone && (
                            <div className="flex mb-2 text-[10px]">
                                <span className="font-bold w-[45px] flex-shrink-0">Phone:</span>
                                <span className="text-black ml-1.5">{phone}</span>
                            </div>
                        )}
                        {email && (
                            <div className="flex mb-2 text-[10px]">
                                <span className="font-bold w-[45px] flex-shrink-0">Email:</span>
                                <span className="text-black ml-1.5 break-all">{email}</span>
                            </div>
                        )}
                        {linkedin && (
                            <div className="flex mb-2 text-[10px]">
                                <span className="font-bold w-[45px] flex-shrink-0">LinkedIn:</span>
                                <span className="text-black ml-1.5 break-all">{linkedin}</span>
                            </div>
                        )}
                        {address && (
                            <div className="flex mb-2 text-[10px]">
                                <span className="font-bold w-[45px] flex-shrink-0">Address:</span>
                                <span className="text-black ml-1.5">{address}</span>
                            </div>
                        )}
                    </div>

                    {/* EDUCATION Section */}
                    {education && education.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                                EDUCATION
                            </h2>
                            <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-3.5"></div>

                            {education.map((edu, idx) => {
                                const degree = (edu?.education_course?.title || "").toUpperCase();
                                const school = edu?.insitute_university || "";
                                const startYear = edu?.course_duration_start_year || "";
                                const endYear = edu?.course_duration_end_year || "";
                                const period = `${startYear} - ${endYear}`;
                                const specialty = edu?.education_course_specialty?.title;
                                const university = edu?.university_name;
                                const marks = edu?.marks;

                                const details = [
                                    university,
                                    specialty ? `Specialization: ${specialty}` : null,
                                    marks ? `Marks: ${marks}` : null
                                ].filter(Boolean).join(", ");

                                return (
                                    <div key={idx} className="mb-4">
                                        <p className="text-[11px] font-bold text-black">{degree}</p>
                                        <p className="text-[10px] text-black">{school}</p>
                                        {details && <p className="text-[9px] text-black">{details}</p>}
                                        <p className="text-[9px] text-[#666666]">{period}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* SKILLS Section */}
                    {it_skills && it_skills.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                                SKILLS
                            </h2>
                            <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-3.5"></div>

                            {it_skills.map((skill, idx) => {
                                const skillName = skill?.skill?.title || skill?.skill || "";
                                const experience = formatExperience(skill?.experience_year, skill?.experience_month);
                                const level = calculateSkillLevel(skill?.experience_year, skill?.experience_month);

                                return (
                                    <div key={idx} className="mb-2">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-[10px] font-bold text-black">{skillName}</span>
                                            {experience && (
                                                <span className="text-[9px] text-[#666666]">{experience}</span>
                                            )}
                                        </div>
                                        <div className="relative w-full h-[6px] bg-[#E0E0E0]">
                                            <div
                                                className="absolute top-0 left-0 h-full bg-black"
                                                style={{ width: `${level}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* LANGUAGES Section */}
                    {candidate_languages && candidate_languages.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                                LANGUAGES
                            </h2>
                            <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-3.5"></div>

                            {candidate_languages.map((lang, idx) => {
                                const languageName = lang?.language?.title || lang?.language || "";
                                const proficiency = getLanguageProficiency(lang);

                                if (!languageName) return null;

                                return (
                                    <div key={idx} className="mb-2">
                                        <p className="text-[10px] font-bold text-black">{languageName}</p>
                                        <p className="text-[9px] text-[#666666]">{proficiency}</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Right Content - 65% width */}
                <div className="w-[65%] px-8 py-5 bg-white">
                    {/* CERTIFICATIONS Section */}
                    {certifications && certifications.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                                CERTIFICATIONS
                            </h2>
                            <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-3.5"></div>

                            {certifications.map((cert, idx) => {
                                const certName = cert?.certification_name || "Certification Name";
                                const certNumber = cert?.certification_number;
                                const certDate = formatDate(cert?.certification_date);
                                const certValidity = formatDate(cert?.certification_validity);
                                const certIssuer = cert?.certification_authority;

                                return (
                                    <div key={idx} className="mb-4">
                                        <p className="text-xs font-bold text-black">{certName}</p>
                                        {certNumber && (
                                            <p className="text-[10px] text-black">ID: {certNumber}</p>
                                        )}
                                        {certDate && (
                                            <p className="text-[10px] text-[#666666]">Date: {certDate}</p>
                                        )}
                                        {certValidity && (
                                            <p className="text-[10px] text-[#666666]">Valid until: {certValidity}</p>
                                        )}
                                        {certIssuer && (
                                            <p className="text-[10px] text-[#666666]">Issuer: {certIssuer}</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* PROJECTS Section */}
                    {projects && projects.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                                PROJECTS
                            </h2>
                            <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-3.5"></div>

                            {projects.map((project, idx) => {
                                const projectName = project?.project_tag || "Project Name";
                                const role = project?.role;
                                const description = project?.project_description;
                                const technologies = project?.skill_used;
                                const duration = `${formatMonthYear(project?.work_from_year, project?.work_from_month)} - ${formatMonthYear(project?.work_to_year, project?.work_to_month)}`;

                                return (
                                    <div key={idx} className="mb-4">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className="text-xs font-bold text-black flex-1">{projectName}</p>
                                            {duration && duration !== " - " && (
                                                <p className="text-[10px] text-[#666666]">{duration}</p>
                                            )}
                                        </div>
                                        {role && (
                                            <p className="text-[10px] text-black">Role: {role}</p>
                                        )}
                                        {description && (
                                            <p className="text-[10px] text-black leading-[1.3] text-justify mt-1">
                                                {description}
                                            </p>
                                        )}
                                        {technologies && (
                                            <p className="text-[10px] text-[#666666] mt-1">
                                                Technologies: {technologies}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* EXPERIENCE Section (if needed) */}
                    {employment && employment.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-sm font-bold tracking-wide text-black uppercase">
                                EXPERIENCE
                            </h2>
                            <div className="w-[30px] h-[2px] bg-black mt-1.5 mb-3.5"></div>

                            {employment.map((job, idx) => {
                                const position = (job?.job_title?.title || "").toUpperCase();
                                const company = job?.current_company?.title || job?.current_company;
                                const startYear = job?.start_year;
                                const endYear = job?.is_current_employement === 1 || job?.is_current_employement === "1"
                                    ? "Present"
                                    : job?.end_year;
                                const period = `${startYear} - ${endYear}`;
                                const jobProfile = job?.job_profile;

                                return (
                                    <div key={idx} className="mb-4">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className="text-xs font-bold text-black flex-1">{position}</p>
                                            <p className="text-[10px] text-[#666666]">{period}</p>
                                        </div>
                                        <p className="text-[10px] text-black">{company}</p>
                                        {jobProfile && (
                                            <p className="text-[10px] text-black leading-[1.3] text-justify mt-1">
                                                {jobProfile}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExecutiveTemplate;
